import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  RefreshIcon,
  Text,
  Badge,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  EditIcon,
} from '@razorpay/blade/components';
import React, { useState } from 'react';
import { useDetailsView, useSalaryComponentHelpers } from 'components/SalaryComponents/hooks';
import { ActionModals } from 'components/SalaryComponents/modals';
import { useQuery } from 'react-query';
import { salaryComponentParams } from '../../constants';

import { EmptyView } from '../../components/EmptyView';
import {
  PayrollGetPerquisiteComponentResponse,
  PerquisitesComponent,
} from 'api/gql-utils/perquisitesComponents/types';
import getPerquisiteComponent from 'api/gql-utils/perquisitesComponents/getPerquisiteComponent';
import { AppError } from 'utils/AppError';
import { SalaryComponent } from 'components/SalaryComponents/types';

const PerquisitesDetailView = () => {
  const { paramId: salaryComponentId, closeDetailsView } = useDetailsView(
    salaryComponentParams.componentId,
  );
  const { openPredefinedEditPage, openPerquisitesEditPage } = useSalaryComponentHelpers();
  const [currentActiveModal, setCurrentActiveModal] = useState<string | null>(null);
  const isOpen = !!salaryComponentId;

  const { data, isLoading, refetch, isError } = useQuery<
    PayrollGetPerquisiteComponentResponse,
    AppError,
    PerquisitesComponent
  >({
    queryKey: [getPerquisiteComponent, salaryComponentId],
    queryFn: () => getPerquisiteComponent({ id: salaryComponentId || '' }),
    select(data) {
      return data.payrollGetPerquisiteComponentDetails.data;
    },
  });

  const openModal = (modal: string) => {
    setCurrentActiveModal(modal);
  };

  const closeModal = () => {
    setCurrentActiveModal(null);
  };

  const onDismiss = () => {
    closeModal();
    closeDetailsView();
  };

  // Using updated response structure: top-level fields like description, displayName, status

  const handleEditClick = () => {
    if (!salaryComponentId) return;
    openPerquisitesEditPage(salaryComponentId);
  };

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Text>Loading details</Text>
        </Box>
      ) : (
        <Drawer isOpen={isOpen} onDismiss={onDismiss} zIndex={900}>
          <DrawerHeader
            title={data?.name ?? ''}
            subtitle={data?.description}
            titleSuffix={
              data?.status === 'active' ? (
                <Badge color="positive">Active</Badge>
              ) : (
                <Badge color="information">Inactive</Badge>
              )
            }
          />

          <DrawerBody>
            {isError && (
              <EmptyView
                isError
                description={
                  <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
                    Due to an unknown error, we could not load the component details page.
                  </Text>
                }>
                <Box display={'flex'} justifyContent={'center'}>
                  <Box width={'104px'}>
                    <Button icon={RefreshIcon} size={'small'} onClick={() => refetch()}>
                      Retry
                    </Button>
                  </Box>
                </Box>
              </EmptyView>
            )}

            {data && !isLoading && (
              <Box display="flex" flexDirection="column" gap="spacing.6" flex="1">
                {/* Details Section */}
                <Box display="flex" flexDirection="column" gap="spacing.6">
                  {/* General Details Header */}
                  <Box display="flex" flexDirection="row" gap="spacing.2" alignItems="center">
                    <Text size="medium" weight="semibold" color="surface.text.gray.subtle">
                      General
                    </Text>
                    <Text size="medium" weight="regular" color="surface.text.gray.muted">
                      details
                    </Text>
                  </Box>

                  {/* Details Content */}
                  <Box display="flex" flexDirection="column" gap="spacing.3">
                    <InfoGroup itemOrientation="horizontal" size="medium" valueAlign="left">
                      <InfoItem>
                        <InfoItemKey>Component Name</InfoItemKey>
                        <InfoItemValue>
                          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                            {data?.name || ''}
                          </Text>
                        </InfoItemValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoItemKey>Display name</InfoItemKey>
                        <InfoItemValue>
                          <Text size="medium" weight="regular" color="surface.text.gray.subtle">
                            {data?.displayName || ''}
                          </Text>
                        </InfoItemValue>
                      </InfoItem>
                    </InfoGroup>
                  </Box>
                </Box>
                {/* Footer Actions */}
                <Box
                  padding="spacing.6"
                  borderTopWidth="thin"
                  borderTopColor="surface.border.gray.muted"
                  marginTop="spacing.6"
                  position={'absolute'}
                  bottom={'spacing.0'}
                  width={'100%'}
                  marginLeft={'-20px'}>
                  <Box display="flex" flexDirection="row" gap="spacing.4" alignItems="center">
                    <Button
                      isFullWidth={true}
                      size="large"
                      iconPosition="left"
                      icon={EditIcon}
                      accessibilityLabel="Modify component"
                      onClick={handleEditClick}>
                      Modify component
                    </Button>
                    <Button
                      variant="tertiary"
                      size="large"
                      accessibilityLabel={data?.status === 'active' ? 'Disable' : 'Enable'}
                      onClick={() => openModal(data?.status === 'active' ? 'disable' : 'enable')}>
                      {data?.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </DrawerBody>
        </Drawer>
      )}

      {data && (
        <ActionModals
          salaryComponent={data as SalaryComponent}
          currentActiveModal={currentActiveModal}
          closeModal={closeModal}
          enableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once enabled, you can add the component to an active employee's compensation
                </Text>
              </Box>
            </Box>
          }
          disableModalContent={
            <Box display={'flex'} flexDirection={'column'} gap={'spacing.5'}>
              <Box display={'flex'} flexDirection={'column'} gap={'spacing.2'}>
                <Text size="small" color={'surface.text.gray.muted'} weight={'medium'}>
                  What will happen?
                </Text>
                <Text color={'surface.text.gray.subtle'}>
                  Once disabled, it cannot be used in an employee's compensation
                </Text>
              </Box>
            </Box>
          }
        />
      )}
    </>
  );
};

export default PerquisitesDetailView;
